import React from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  Assignment as FormIcon,
  Assessment as AuditIcon,
  BarChart as ReportIcon,
  People as UsersIcon,
  AccountCircle as ProfileIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const DRAWER_WIDTH = 240;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  const { user, logout } = useAuth();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
  };

  // Menú de navegación según especificación:
  // - Admin: acceso total
  // - Manager: Dashboard, Auditorías, Reportes
  // - Auditor: Dashboard, Auditorías
  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', roles: ['admin', 'manager', 'auditor'] },
    { text: 'Negocios', icon: <BusinessIcon />, path: '/business', roles: ['admin'] },
    { text: 'Formularios', icon: <FormIcon />, path: '/forms', roles: ['admin'] },
    { text: 'Auditorías', icon: <AuditIcon />, path: '/audits', roles: ['admin', 'auditor', 'manager'] },
    { text: 'Reportes', icon: <ReportIcon />, path: '/reports', roles: ['admin', 'manager'] },
    { text: 'Usuarios', icon: <UsersIcon />, path: '/users', roles: ['admin'] },
  ];

  const filteredMenuItems = menuItems.filter(item => 
    item.roles.includes(user?.role || '')
  );

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          AuditPro
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {filteredMenuItems.map((item) => (
          <ListItem
            key={item.text}
            component={Link}
            to={item.path}
            selected={location.pathname === item.path}
            onClick={() => {
              if (isMobile) {
                handleDrawerToggle();
              }
            }}
            sx={{
              color: 'inherit',
              textDecoration: 'none',
              '&.Mui-selected': {
                backgroundColor: '#000000',        // Fondo negro
                color: '#ffeb5d',                  // Texto amarillo
                '&:hover': {
                  backgroundColor: '#1a1a1a',      // Negro más claro en hover
                },
              },
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)', // Hover suave en items no seleccionados
              },
            }}
          >
            <ListItemIcon sx={{ 
              color: location.pathname === item.path ? '#ffeb5d' : 'inherit' 
            }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
    </div>
  );

  if (!user) {
    return <>{children}</>;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        className="layout-main-header"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
        }}
      >
        <Toolbar sx={{ 
          minHeight: { xs: 52, sm: 64 },
          px: { xs: 0.5, sm: 2 }, // Reducido padding izquierdo en mobile
        }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: { xs: 0.5, sm: 2 }, 
              ml: { xs: 0.5, sm: 0 }, // Margen izquierdo en mobile
              display: { md: 'none' },
              p: { xs: 1.25, sm: 1 }, // Reducido padding del botón
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
              '&:active': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
              }
            }}
          >
            <MenuIcon sx={{ fontSize: { xs: 30, sm: 28 } }} />
          </IconButton>
          
          {/* Logo de la empresa - alineado a la izquierda, máximo tamaño */}
          <Box 
            component="img" 
            src="/logo.png" 
            alt="AuditPro Logo" 
            sx={{ 
              height: { xs: 43, sm: 54 }, // Reducido 10% (de 48→43px mobile, 60→54px desktop)
              width: 'auto',
              maxWidth: { xs: '50%', sm: '300px' }, // Limitar ancho máximo
              objectFit: 'contain',
              mr: { xs: 1, sm: 2 },
            }}
            onError={(e) => {
              // Si el logo no carga, ocultar el elemento
              e.currentTarget.style.display = 'none';
              console.warn('Logo no encontrado en /logo.png');
            }}
          />

          <Box sx={{ flexGrow: 1 }} /> {/* Spacer para empujar el usuario a la derecha */}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {user?.name} ({user?.role})
            </Typography>
            <IconButton
              size="medium"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
              sx={{ 
                p: 1,
                width: 40,
                height: 40,
                mr: { xs: 0, sm: 0 }, // Sin margen extra, el padding del Toolbar lo maneja
              }}
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                {user?.name?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={handleClose} component={Link} to="/profile">
                <ListItemIcon>
                  <ProfileIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Mi Perfil</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Cerrar Sesión</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          display: { xs: 'block', md: 'block' },
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawer}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 0, md: 3 },
          width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
          maxWidth: '100vw',
          overflow: 'hidden'
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;